// Dolap durum degistir — admin'in dolabin durumunu zorla degistirmesi icin.
// MUSAIT secilirse aktif kiralama + bekleyen rezervasyon kapatilir (atomic).
// Diger durumlar icin sadece dolap.durum guncellenir.

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { useDolapZorlaSifirla } from './api.js';

type Hedef = 'MUSAIT' | 'MESGUL' | 'REZERVE' | 'BAKIM' | 'CEVRIMDISI';

const HEDEF_ETIKET: Record<Hedef, string> = {
  MUSAIT: 'Müsait yap',
  MESGUL: 'Meşgul olarak işaretle',
  REZERVE: 'Rezerve olarak işaretle',
  BAKIM: 'Bakıma al',
  CEVRIMDISI: 'Çevrimdışı olarak işaretle',
};

const HEDEF_ACIKLAMA: Record<Hedef, string> = {
  MUSAIT:
    'Aktif kiralama veya rezervasyon varsa hepsi otomatik kapatılır (TAMAMLANDI). Para iadesi yapılmaz — gerekirse manuel iade.',
  MESGUL: 'Sadece durum güncellenir. Mevcut kiralamalar etkilenmez.',
  REZERVE: 'Sadece durum güncellenir. Bağlanan rezervasyon kaydı oluşmaz.',
  BAKIM: 'Müşterilere müsait gözükmez. Mevcut kiralamaları etkilemez.',
  CEVRIMDISI: 'Müşterilere müsait gözükmez. Cihaz erişilemez işaretlenir.',
};

export function ZorlaSifirlaDialog({
  acik,
  onDegisim,
  dolapId,
  mevcutDurum,
}: {
  acik: boolean;
  onDegisim: (v: boolean) => void;
  dolapId: string;
  mevcutDurum?: string;
}) {
  const [hedef, setHedef] = useState<Hedef>('MUSAIT');
  const [sebep, setSebep] = useState('');
  const sifirla = useDolapZorlaSifirla();

  const onayla = async () => {
    if (sebep.trim().length < 3) {
      toast.error('En az 3 karakter sebep yazın.');
      return;
    }
    try {
      await sifirla.mutateAsync({ id: dolapId, sebep: sebep.trim(), yeniDurum: hedef });
      toast.success(`Dolap ${hedef} olarak güncellendi`);
      setSebep('');
      onDegisim(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const aktifKapanacak = hedef === 'MUSAIT';

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="flex items-center gap-2 text-base font-semibold text-red-600 dark:text-red-400">
                <AlertTriangle size={16} /> Dolap Durumunu Değiştir
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex items-center gap-2 text-sm">
                  Mevcut durum:{' '}
                  <Rozet ton="fume" className="font-mono">
                    {mevcutDurum ?? '?'}
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Etiket htmlFor="hedef">Yeni durum</Etiket>
              <select
                id="hedef"
                value={hedef}
                onChange={(e) => setHedef(e.target.value as Hedef)}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
              >
                {(Object.keys(HEDEF_ETIKET) as Hedef[]).map((h) => (
                  <option key={h} value={h}>
                    {HEDEF_ETIKET[h]}
                  </option>
                ))}
              </select>
              <p
                className={`text-xs ${aktifKapanacak ? 'font-medium text-red-600 dark:text-red-400' : 'text-fume-500 dark:text-fume-400'}`}
              >
                {HEDEF_ACIKLAMA[hedef]}
              </p>
            </div>

            <div className="space-y-1.5">
              <Etiket htmlFor="sebep">Sebep (denetim için zorunlu)</Etiket>
              <Girdi
                id="sebep"
                placeholder="Örn: Müşteri ödemeyi tamamlamadı, dolap takılı kaldı"
                value={sebep}
                onChange={(e) => setSebep(e.target.value)}
                maxLength={500}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" disabled={sifirla.isPending}>
                  Vazgeç
                </Buton>
              </Dialog.Close>
              <Buton
                onClick={() => void onayla()}
                disabled={sifirla.isPending || sebep.trim().length < 3}
                className={aktifKapanacak ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {sifirla.isPending ? 'İşleniyor…' : HEDEF_ETIKET[hedef]}
              </Buton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
